import { defineComponent, ref } from 'vue';
import { useWindowSize, useEventListener } from '@vueuse/core';
import { useTNodeJSX } from '../hooks/tnode';
import TLoading from '../loading';
import props from './props';
import { useScrollParent } from '../shared';
import { usePrefixClass, useConfig } from '../hooks/useClass';

export default defineComponent({
  name: 'TList',
  components: {
    TLoading,
  },
  props,
  emits: ['load-more', 'scroll'],
  setup(props, { slots }) {
    const listClass = usePrefixClass('list');
    const { globalConfig } = useConfig('list');
    const renderTNodeJSX = useTNodeJSX();

    const LOADING_TEXT_MAP = {
      loading: globalConfig.value.loading,
      'load-more': globalConfig.value.loadingMoreText,
    };

    const root = ref<HTMLElement>();
    const scrollParent = useScrollParent(root);
    const { height } = useWindowSize();

    const onLoadMore = (e: MouseEvent) => {
      if (props.asyncLoading === 'load-more') {
        props.onLoadMore?.();
      }
    };
    const handleScroll = (e: WheelEvent | Event) => {
      const scrollHeight =
        (e.target as HTMLElement).scrollHeight ||
        Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
      const scrollTop =
        (e.target as HTMLElement).scrollTop || document.documentElement.scrollTop || document.body.scrollTop;
      const offsetHeight = (e.target as HTMLElement).offsetHeight || height.value;

      props.onScroll?.(scrollHeight - (scrollTop + offsetHeight), scrollTop);
    };

    const renderLoading = () => {
      if (typeof props.asyncLoading === 'string' && ['loading', 'load-more'].includes(props.asyncLoading)) {
        return (
          <TLoading
            indicator={props.asyncLoading === 'loading'}
            text={
              typeof props.asyncLoading === 'string'
                ? LOADING_TEXT_MAP[props.asyncLoading as keyof typeof LOADING_TEXT_MAP]
                : ''
            }
            class={`${listClass.value}__loading`}
          />
        );
      }
      return renderTNodeJSX('asyncLoading');
    };

    useEventListener(scrollParent, 'scroll', handleScroll);
    return () => {
      const headerContent = renderTNodeJSX('header');
      const footerContent = renderTNodeJSX('footer');
      return (
        <div ref={root} class={listClass.value} onScroll={handleScroll}>
          {headerContent}
          {slots.default && slots.default()}
          <div class={`${listClass.value}__loading--wrapper`} onClick={onLoadMore}>
            {renderLoading()}
          </div>
          {footerContent}
        </div>
      );
    };
  },
});
